/* ==========================================================================
   <product-carousel>
   --------------------------------------------------------------------------
   The theme's bundled <collection-slider> gives prev/next arrows but has no
   pagination dots (its changeToIndex is an empty stub), and bundle.js ships
   minified with no source in this theme, so this behaviour lives here instead.

   Scroll-snap track + arrows + dots. Add-to-cart and quick view are handled by
   the theme's own <product-form> / <product-card> elements inside each slide.
   ========================================================================== */

(function () {
  if (customElements.get('product-carousel')) return;

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.de-carousel__track');
      this.dotList = this.querySelector('.de-carousel__dots');
      this.prevButton = this.querySelector('.de-carousel__arrow--prev');
      this.nextButton = this.querySelector('.de-carousel__arrow--next');

      if (!this.track) return;

      this.page = 0;
      this.pageCount = 1;

      this.onScroll = this.throttleToFrame(() => this.syncState());
      this.onResize = this.throttleToFrame(() => this.build());

      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollByPage(1));
      }

      // Column widths are percentage-based, so a resize changes the page count.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.build();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    /* Coalesce scroll/resize bursts into one update per animation frame. */
    throttleToFrame(callback) {
      let frame = null;
      return () => {
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          callback();
        });
      };
    }

    get pageWidth() {
      return this.track.clientWidth;
    }

    build() {
      if (!this.track || !this.pageWidth) return;

      // A trailing fraction of a page still needs its own dot, but floating
      // point slop on exact fits must not add an empty one.
      const pages = (this.track.scrollWidth - 1) / this.pageWidth;
      this.pageCount = Math.max(1, Math.ceil(pages - 0.02));

      this.renderDots();
      this.syncState();
    }

    renderDots() {
      if (!this.dotList) return;

      this.dotList.hidden = this.pageCount < 2;

      if (this.dotList.childElementCount === this.pageCount) return;

      this.dotList.textContent = '';

      for (let index = 0; index < this.pageCount; index += 1) {
        const item = document.createElement('li');
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'de-carousel__dot';
        button.dataset.page = String(index);
        button.setAttribute(
          'aria-label',
          (this.dataset.dotLabel || 'Go to slide __INDEX__').replace('__INDEX__', String(index + 1))
        );
        button.addEventListener('click', () => this.scrollToPage(index));

        item.appendChild(button);
        this.dotList.appendChild(item);
      }
    }

    syncState() {
      if (!this.track) return;

      const maxScroll = this.track.scrollWidth - this.pageWidth;
      const scrollLeft = Math.abs(this.track.scrollLeft); // RTL reports negative

      this.page = this.pageWidth ? Math.round(scrollLeft / this.pageWidth) : 0;
      this.page = Math.min(this.page, this.pageCount - 1);

      if (this.dotList) {
        Array.from(this.dotList.querySelectorAll('.de-carousel__dot')).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === this.page ? 'true' : 'false');
        });
      }

      // 1px of tolerance: sub-pixel layout means scrollLeft rarely hits maxScroll exactly.
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= maxScroll - 1;

      if (this.prevButton) {
        this.prevButton.disabled = atStart;
        this.prevButton.setAttribute('aria-disabled', String(atStart));
      }
      if (this.nextButton) {
        this.nextButton.disabled = atEnd;
        this.nextButton.setAttribute('aria-disabled', String(atEnd));
      }
    }

    scrollByPage(direction) {
      this.scrollToPage(this.page + direction);
    }

    scrollToPage(page) {
      const target = Math.max(0, Math.min(page, this.pageCount - 1));
      this.track.scrollTo({ left: target * this.pageWidth, behavior: 'smooth' });
    }
  }

  customElements.define('product-carousel', ProductCarousel);
})();

/* ==========================================================================
   <jump-nav>
   --------------------------------------------------------------------------
   Anchor links that scroll to sections further down the page and underline
   whichever one you are currently looking at.

   Not a tab bar: every target stays in the document, so this only moves the
   viewport and tracks position. Scroll position is read directly rather than
   using IntersectionObserver, because the targets have very different heights
   and "which section is under the header right now" is the question being
   asked, not "which is visible".
   ========================================================================== */

(function () {
  if (customElements.get('jump-nav')) return;

  class JumpNav extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('.de-jumpnav__link'));
      if (!this.links.length) return;

      this.extraOffset = parseInt(this.dataset.offset || '0', 10) || 0;

      this.onScroll = this.throttleToFrame(() => this.syncActive());
      this.onClick = (event) => this.handleClick(event);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);

      this.syncActive();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.removeEventListener('click', this.onClick);
    }

    /* The theme's header group is sticky, so a target scrolled to y=0 would sit
       underneath it. Measured rather than hard-coded: the header's height
       differs between templates and breakpoints. */
    stickyHeaderHeight() {
      const header = document.querySelector('.header-group-wrapper');
      if (!header) return 0;

      const position = getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;

      return Math.round(header.getBoundingClientRect().height);
    }

    offset() {
      return this.stickyHeaderHeight() + this.extraOffset;
    }

    targetFor(link) {
      const href = link.getAttribute('href') || '';
      if (href.length < 2 || href.charAt(0) !== '#') return null;
      try {
        return document.getElementById(decodeURIComponent(href.slice(1)));
      } catch (error) {
        return null;
      }
    }

    handleClick(event) {
      const link = event.target.closest('.de-jumpnav__link');
      if (!link || !this.links.includes(link)) return;

      const target = this.targetFor(link);
      if (!target) return;

      event.preventDefault();

      const top = window.scrollY + target.getBoundingClientRect().top - this.offset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      /* Update the address bar without letting the browser jump there too. */
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', link.getAttribute('href'));
      }

      this.setActive(link);
    }

    setActive(activeLink) {
      this.links.forEach((link) => {
        if (link === activeLink) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    syncActive() {
      const line = this.offset() + 1;
      let active = null;

      this.links.forEach((link) => {
        const target = this.targetFor(link);
        if (!target) return;
        /* The last target whose top has passed the line is the one being read. */
        if (target.getBoundingClientRect().top <= line) active = link;
      });

      /* Above the first target, keep the first link marked rather than none. */
      if (!active) {
        const firstWithTarget = this.links.find((link) => this.targetFor(link));
        active = firstWithTarget || null;
      }

      if (active) this.setActive(active);
    }

    throttleToFrame(callback) {
      let queued = false;
      return () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          callback();
        });
      };
    }
  }

  customElements.define('jump-nav', JumpNav);
})();
